import { useState, useEffect, useRef, useCallback } from 'react';
import { AccessToken } from '../../../utils';
import { getWebSocketUrl } from '../../../utils/websocketUtils';

/**
 * Custom hook for managing admin WebSocket connection for customer updates
 */
export const useAdminUpdatesSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [redisHealth, setRedisHealth] = useState(null);
  const webSocketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const healthCheckTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Debug mode can be enabled with localStorage.debug = "true"
  const debug = process.env.REACT_APP_WS_DEBUG === 'true' || localStorage.getItem("debug") === "true";

  // Handle customer status updates
  const [customerUpdates, setCustomerUpdates] = useState([]);
  
  // Handle job status updates
  const [jobUpdates, setJobUpdates] = useState([]);

  // Send a health check to the server
  const checkHealth = useCallback(() => {
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      if (debug) console.log('Sending health check');
      try {
        webSocketRef.current.send(JSON.stringify({
          type: 'health_check'
        }));
      } catch (error) {
        if (debug) console.error('Error sending health check:', error);
      }
    }
  }, [debug]);

  // Start periodic health checks
  const startHealthChecks = useCallback(() => {
    // Clear any existing timer
    if (healthCheckTimerRef.current) {
      clearInterval(healthCheckTimerRef.current);
    }
    
    // Start new health check interval - every 30 seconds
    healthCheckTimerRef.current = setInterval(() => {
      checkHealth();
    }, 20000); // Reduced from 30s to 20s to be more responsive
    
    // Run an immediate health check
    checkHealth();
  }, [checkHealth]);

  // Connect to the admin WebSocket
  const connectWebSocket = useCallback(() => {
    // Clear any existing reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Stop health checks
    if (healthCheckTimerRef.current) {
      clearInterval(healthCheckTimerRef.current);
      healthCheckTimerRef.current = null;
    }

    // Clean up any existing connection
    if (webSocketRef.current) {
      console.log('WebSocket: Close connection request.');
      webSocketRef.current.close();
      webSocketRef.current = null;
    }

    // Reset connection error state when attempting new connection
    // DISABLED: We don't want to show connection errors
    // setConnectionError(null);
    setRedisHealth(null);

    // Get authentication token
    const token = AccessToken();
    if (!token) {
      // DISABLED: Don't set connection error
      // setConnectionError('Authentication token not available. Please log in again.');
      if (debug) console.log('WebSocket: No auth token available');
      return;
    }

    // Get WebSocket URL using the utility function
    const wsUrl = getWebSocketUrl('/ws/admin/updates/', true);
    
    if (debug) console.log('Connecting to Admin WebSocket:', wsUrl);
    
    try {
      // Create and set up WebSocket
      const ws = new WebSocket(wsUrl);
      webSocketRef.current = ws;
  
      ws.onopen = () => {
        if (debug) console.log('Admin WebSocket connected successfully');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        
        // Start health checks after successful connection
        startHealthChecks();
      };
  
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (debug) console.log('Admin WebSocket Message Received:', data);
          
          // Handle different message types
          if (data.type === 'customer.sync.update' && data.payload) {
            setCustomerUpdates(prev => [...prev, data]);
          } else if (data.type === 'job.started' || data.type === 'job.finished') {
            setJobUpdates(prev => [...prev, data]);
          } else if (data.type === 'authentication_status') {
            if (debug && data.status === 'limited_access') {
              console.log('WebSocket authentication status:', data.message);
            }
          } else if (data.type === 'health_response') {
            setRedisHealth(data.status);
            if (debug && data.status === 'unhealthy' && data.error) {
              console.log(`Redis health issue: ${data.error}`);
            }
          } else if (data.type === 'pong') {
            if (debug) console.log('Received pong from server');
          } else if (data.type === 'connection_status') {
            if (debug) console.log('Connection status:', data.status);
          }
        } catch (error) {
          if (debug) console.error('Error parsing WebSocket message:', error);
        }
      };
  
      ws.onclose = (event) => {
        setIsConnected(false);
        
        const wasClean = event.wasClean;
        const code = event.code;
        const reason = event.reason;
        
        if (debug) {
          console.log(`Admin WebSocket disconnected. Code: ${code}, Clean: ${wasClean}, Reason: ${reason}`);
        }
        
        // Stop health checks on disconnect
        if (healthCheckTimerRef.current) {
          clearInterval(healthCheckTimerRef.current);
          healthCheckTimerRef.current = null;
        }
        
        // Interpret closure codes - DISABLED error popups, only log for debugging
        if (code === 1000) {
          // Normal closure, don't reconnect
          if (debug) console.log('WebSocket closed normally');
          return; // <-- Prevent reconnect logic for normal closure
        } else if (code === 1006) {
          // Abnormal closure
          if (debug) console.log('Connection closed unexpectedly. Attempting to reconnect...');
        } else if (code === 4003) {
          // Our custom code for authentication failure
          if (debug) console.log('Authentication failed. Please refresh the page or log in again.');
          return; // Don't attempt to reconnect on auth failure
        } else if (code === 1013) {
          // Try again later
          if (debug) console.log('Server is busy. Will try to reconnect shortly...');
        } else if (code === 1011) {
          // Internal server error
          if (debug) console.log('Server encountered an error. Attempting to reconnect...');
        }
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(1.5, reconnectAttemptsRef.current), 10000);
          if (debug) {
            console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          }
          
          reconnectTimerRef.current = setTimeout(() => {
            if (document.visibilityState !== 'hidden') {
              reconnectAttemptsRef.current++;
              connectWebSocket();
            }
          }, delay);
        } else {
          if (debug) console.log('Could not reconnect after multiple attempts. Please refresh the page.');
        }
      };
  
      ws.onerror = (error) => {
        if (debug) console.error('Admin WebSocket error:', error);
        // DISABLED: Don't set connection error
        // setConnectionError('Connection error occurred. The system will try to reconnect automatically.');
      };
    } catch (error) {
      if (debug) console.error('Error creating WebSocket:', error);
      // DISABLED: Don't set connection error
      // setConnectionError('Failed to create WebSocket connection: ' + error.message);
      
      // Schedule a reconnection attempt
      reconnectTimerRef.current = setTimeout(() => {
        if (document.visibilityState !== 'hidden' && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          connectWebSocket();
        }
      }, 2000);
    }
  }, [debug, startHealthChecks]); // Removed connectionError dependency as we don't use it anymore

  // Connect on component mount and disconnect on unmount
  useEffect(() => {
    connectWebSocket();
    
    // Auto-reconnect when window regains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        connectWebSocket();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clear any pending reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      
      // Clear health check interval
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
      }
      
      // Close any existing connection
      if (webSocketRef.current) {
        webSocketRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send a ping message to the server
  const ping = useCallback(() => {
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      try {
        webSocketRef.current.send(JSON.stringify({ type: 'ping' }));
        return true;
      } catch (error) {
        console.error('Error sending ping:', error);
        return false;
      }
    }
    return false;
  }, []);

  // Reset update arrays
  const clearUpdates = useCallback(() => {
    setCustomerUpdates([]);
    setJobUpdates([]);
  }, []);

  return {
    isConnected,
    redisHealth,
    customerUpdates,
    jobUpdates,
    clearUpdates,
    ping,
    checkHealth,
    reconnect: connectWebSocket
  };
}; 